import { AuthError, EventMessage, EventType } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router } from "react-router-dom";
import { useUserApi } from "~/api/GeneratedApi";
import { setUser } from "~/redux/reducers/authReducer";
import { useAppDispatch } from "~/redux/store/store";
import { publicRoutes } from "~/routes";
import LoadingIndicator from "~/template/components/LoadingIndicator";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import useAuth from "~/utils/hooks/useAuth";
import { useViewportSize } from "~/utils/hooks/useViewportSize";
import Root from "./Root";

const AuthWrapper = (): JSX.Element => {
  const { isLoggedIn, user } = useAuth();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("login");
  const apiErrorHandler = useApiErrorHandler();
  useViewportSize();
  const [userLoading, setUserLoading] = useState(true);

  const { instance, inProgress } = useMsal();

  const userApi = useUserApi();

  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (!isAuthenticated || user) return;
    setUserLoading(true);
    userApi
      .login()
      .then((userDto) => {
        if (!userDto.id) return;
        dispatch(
          setUser({
            username: userDto.name,
            email: userDto.email,
            id: userDto.id,
            organization: userDto.organization!,
            isAdmin: userDto.globalAdmin,
            isOrganizationAdmin:
              userDto.organizationRole?.organizationAdmin ?? false,
            alarmsMuted: userDto.alarmsMuted,
            arrivalsMuted: userDto.arrivalsMuted,
          }),
        );
      })
      .catch(apiErrorHandler)
      .finally(() => setUserLoading(false));
  }, [isAuthenticated, apiErrorHandler, dispatch, userApi, user]);

  useEffect(() => {
    const acc = instance.getAllAccounts()?.[0];
    if (!acc) return;
    instance.setActiveAccount(acc);
  }, [isAuthenticated, instance, dispatch]);

  useEffect(() => {
    const id = instance.addEventCallback((message: EventMessage) => {
      if (message.eventType !== EventType.LOGIN_FAILURE) return;

      if (!(message.error instanceof AuthError)) return;
      enqueueSnackbar(
        t("LoginFailed", { errorcode: message.error.errorCode }),
        { variant: "error" },
      );
    });

    return function cleanup() {
      if (id) instance.removeEventCallback(id);
    };
  }, [instance, t, enqueueSnackbar, user, userApi, dispatch, apiErrorHandler]);

  if (
    (!isAuthenticated && ["handleRedirect", "startup"].includes(inProgress)) ||
    (userLoading && isAuthenticated && !user)
  )
    return <LoadingIndicator />;

  return <Router>{isLoggedIn() && user ? <Root /> : publicRoutes}</Router>;
};

export default AuthWrapper;
